import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import Footer from "@/pages/home/components/Footer";
import { CAPABILITY_CATEGORIES, JOB_CATEGORIES } from "@/mocks/capabilities";

export default function CapabilitiesPage() {
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>([]);
  const [matchCount, setMatchCount] = useState<number | null>(null);

  const toggle = (catId: string, skill: string) => {
    setMatchCount(null);
    setSelected((prev) => {
      const cur = prev[catId] || [];
      return { ...prev, [catId]: cur.includes(skill) ? cur.filter((s) => s !== skill) : [...cur, skill] };
    });
  };

  const toggleJob = (id: string) => {
    setMatchCount(null);
    setSelectedJobTypes((p) => (p.includes(id) ? p.filter((j) => j !== id) : [...p, id]));
  };

  const totalSelected = Object.values(selected).flat().length + selectedJobTypes.length;

  const handleFind = () => {
    setMatchCount(Math.min(120 + totalSelected * 47, 847));
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3.5 py-1.5 rounded-full mb-4">
            Capability Matching
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            Tell Us What <span className="text-emerald-500">You Can Do</span>
          </h1>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Forget degrees and resumes. Select your real-world skills and we'll instantly surface income opportunities that match — right now, today.
          </p>
        </div>
      </section>

      {/* Capability Builder */}
      <section className="py-14 bg-[#fafafa]">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            {CAPABILITY_CATEGORIES.map((cat) => {
              const catSel = selected[cat.id] || [];
              return (
                <div key={cat.id} className={`rounded-2xl border ${cat.borderColor} ${cat.bgColor} p-6`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-white border ${cat.borderColor}`}>
                      <i className={`${cat.icon} text-xl ${cat.iconColor}`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm">{cat.title}</h3>
                      <span className="text-xs text-gray-500">{catSel.length} of {cat.skills.length} selected</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {cat.skills.map((skill) => {
                      const active = catSel.includes(skill);
                      return (
                        <button key={skill} onClick={() => toggle(cat.id, skill)}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                            active ? `${cat.badgeColor} border-transparent` : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}>
                          {active && <i className="ri-check-line mr-1" />}{skill}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Job type interest */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <h3 className="font-bold text-gray-900 text-sm mb-1">What type of work interests you?</h3>
            <p className="text-xs text-gray-500 mb-4">Select all that apply</p>
            <div className="flex flex-wrap gap-2">
              {JOB_CATEGORIES.map((job) => {
                const active = selectedJobTypes.includes(job.id);
                return (
                  <button key={job.id} onClick={() => toggleJob(job.id)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl border transition-all cursor-pointer whitespace-nowrap ${
                      active ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-gray-200 text-gray-600 hover:border-emerald-300 hover:text-emerald-600"
                    }`}>
                    <i className={job.icon} />{job.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Match CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-2xl border border-gray-200 p-6">
            <div>
              {matchCount !== null ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-full">
                    <i className="ri-check-double-line text-emerald-600 text-lg" />
                  </div>
                  <div>
                    <div className="font-extrabold text-gray-900 text-xl">{matchCount} Opportunities Found!</div>
                    <Link to="/find-work" className="text-sm text-emerald-600 font-semibold hover:underline">Browse all matches →</Link>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-bold text-gray-900 text-base">
                    {totalSelected > 0 ? `${totalSelected} capabilities selected — ready to match!` : "Select at least one capability to begin"}
                  </div>
                  <div className="text-sm text-gray-500">More selections = better matches and higher pay</div>
                </div>
              )}
            </div>
            <button onClick={handleFind} disabled={totalSelected === 0}
              className={`px-8 py-3.5 font-bold text-sm rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                totalSelected > 0 ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}>
              <i className="ri-flashlight-line mr-2" />Find My Matches
            </button>
          </div>
        </div>
      </section>

      {/* Why it works section */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Why Capabilities Beat Resumes</h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">Traditional hiring filters out real talent. We fix that.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: "ri-timer-2-line", title: "3× Faster to Apply", desc: "Capability profiles take 3 minutes to build. A resume takes 3 hours. We respect your time.", color: "bg-amber-50 text-amber-500" },
              { icon: "ri-equalizer-line", title: "Fair Matching", desc: "No degree? No problem. If you can do the job, Gig n' Cash will find you the job — full stop.", color: "bg-emerald-50 text-emerald-600" },
              { icon: "ri-line-chart-line", title: "Earn More Over Time", desc: "Our Upskill Engine tracks your profile and suggests certifications that directly increase your pay rate.", color: "bg-sky-50 text-sky-600" },
            ].map((f) => (
              <div key={f.title} className="p-6 rounded-2xl border border-gray-100 bg-[#fafafa]">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${f.color} mb-4`}>
                  <i className={`${f.icon} text-lg`} />
                </div>
                <h3 className="font-extrabold text-gray-900 text-sm mb-2">{f.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-500">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">Skills + AI = Income, Today.</h2>
          <p className="text-emerald-100 mb-7 max-w-md mx-auto">Browse real jobs matched to what you just selected.</p>
          <Link to="/find-work" className="inline-block px-8 py-3.5 bg-white text-emerald-700 font-bold rounded-xl text-sm hover:bg-emerald-50 transition-colors whitespace-nowrap">
            <i className="ri-search-line mr-2" />Browse Matched Jobs
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
